import React from 'react';

import hill from 'assets/BackgroundImages/Hill.svg';
import mountain from 'assets/BackgroundImages/Mountain.svg';
import river from 'assets/BackgroundImages/River.svg';
import { matchPath } from 'react-router-dom';

import { ADMIN_WORKSPACE_PATHS } from 'components/layout/adminNavigation';
import { cn } from 'lib/utils';
import './learner.css';

export type RouteTheme = {
  key: string;
  bodyClass: string;
  wrapperClass?: string;
  match: (pathname: string) => boolean;
  // Pages that should use the whole screen width instead of the shared `screen-xl` frame.
  isFullWidth?: (pathname: string) => boolean;
  // Must paint an opaque full-screen base (RouteBackground crossfades whole layers over each other).
  renderBackground?: () => React.ReactNode;
  // Themes whose backgrounds render identically share a key so navigation between them keeps the painted background.
  backgroundKey?: string;
};

const AUTH_ROUTES = ['/login', '/register', '/terms', '/privacy'];
const ADMIN_PREFIXES = ['/admin'];
const FLOW_PREFIXES = ['/flows', '/daily-checkin', '/daily-checkin-complete', '/course', '/quiz-results'];

const SKY_BLUE = 'bg-gradient-to-br from-surface-sky-start to-surface-sky-end';
const SAND_VIOLET = 'bg-gradient-to-br from-surface-sand-start to-surface-sand-end';
const VIOLET_SAND = 'bg-gradient-to-br from-surface-sand-end to-surface-sand-start';
const BLUE_SAND = 'bg-gradient-to-br from-surface-blue-sand-start to-surface-sand-start';
const COHORT_PAPER_SAND = 'bg-gradient-to-br from-surface-cohort-start to-surface-cohort-end';

const renderDefaultBackground = () => (
  <>
    <div className={cn('h-full w-full', VIOLET_SAND)} />
    <img
      className="absolute inset-x-0 bottom-0 h-[80vh] w-full object-cover object-bottom opacity-80"
      src={river}
      alt="" aria-hidden="true"
    />
  </>
);

export const THEME_CONFIGS: RouteTheme[] = [
  {
    key: 'auth',
    bodyClass: 'theme-auth',
    match: (pathname) => AUTH_ROUTES.some((route) => pathname.startsWith(route)),
    renderBackground: () => (
      <>
        <div className={cn('h-full w-full', SAND_VIOLET)} />
        <img
          className="absolute inset-x-0 bottom-0 h-auto w-full object-cover object-bottom opacity-80"
          src={river}
          alt="" aria-hidden="true"
        />
      </>
    ),
  },
  {
    key: 'admin',
    bodyClass: 'theme-admin',
    match: (pathname) => ADMIN_PREFIXES.some((prefix) => pathname.startsWith(prefix)),
    // Only the sidebar workspaces go full width; theme previews and unknown admin paths keep the standard frame.
    isFullWidth: (pathname) => ADMIN_WORKSPACE_PATHS.some((path) => matchPath({ path, end: true }, pathname) !== null),
    renderBackground: () => (
      <div className={cn('h-full w-full', SAND_VIOLET)} />
    ),
  },
  {
    key: 'home',
    bodyClass: 'theme-home',
    wrapperClass: 'learner-surface',
    match: (pathname) => ['/home', '/snapshot'].includes(pathname),
    renderBackground: () => (
      <>
        <div className={cn('h-full w-full', BLUE_SAND)} />
        <img
          className="absolute inset-x-0 bottom-0 h-auto w-full object-cover object-bottom opacity-80"
          src={hill}
          alt="" aria-hidden="true"
        />
      </>
    ),
  },
  {
    key: 'training',
    bodyClass: 'theme-default',
    wrapperClass: 'learner-surface',
    match: (pathname) => pathname === '/training' || pathname.startsWith('/training/'),
    renderBackground: renderDefaultBackground,
    backgroundKey: 'default',
  },
  {
    key: 'cohort',
    bodyClass: 'theme-cohort',
    match: (pathname) => pathname === '/mentorship' || pathname.startsWith('/mentorship/'),
    renderBackground: () => (
      <>
        <div className={cn('h-full w-full', COHORT_PAPER_SAND)} />
        <img
          className="absolute inset-x-0 bottom-0 h-[80vh] w-full object-cover object-bottom opacity-[0.14] saturate-50"
          src={river}
          alt="" aria-hidden="true"
        />
      </>
    ),
  },
  {
    key: 'course',
    bodyClass: 'theme-course',
    match: (pathname) => FLOW_PREFIXES.some((prefix) => pathname.startsWith(prefix)),
    renderBackground: () => (
      <>
        <div className={cn('h-full w-full', SKY_BLUE)} />
        <img
          className="absolute bottom-0 right-0 h-[90vh] w-[100vw] object-bottom"
          src={mountain}
          alt="" aria-hidden="true"
        />
      </>
    ),
  },
  {
    key: 'roleplay',
    bodyClass: 'theme-roleplay',
    match: (pathname) => pathname.startsWith('/roleplay'),
    renderBackground: () => (
      <div className={cn('h-full w-full', SKY_BLUE)} />
    ),
  },
  {
    key: 'default',
    bodyClass: 'theme-default',
    match: () => true,
    renderBackground: renderDefaultBackground,
    backgroundKey: 'default',
  },
];

const BODY_THEME_CLASSES = THEME_CONFIGS.map((theme) => theme.bodyClass).filter(Boolean);

export const getThemeForPath = (pathname: string): RouteTheme => {
  const normalizedPath = pathname || '/';
  return THEME_CONFIGS.find((theme) => theme.match(normalizedPath)) ?? THEME_CONFIGS[THEME_CONFIGS.length - 1];
};

export const syncBodyTheme = (activeClass?: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  BODY_THEME_CLASSES.forEach((themeClass) => {
    if (themeClass) {
      document.body.classList.remove(themeClass);
    }
  });

  if (activeClass) {
    document.body.classList.add(activeClass);
  }
};

